// Content for Śrīmad-Bhāgavatam 1.2.8 commentary topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.2.8',
        sloka: `dharmaḥ svanuṣṭhitaḥ puṁsāṁ
viṣvaksena-kathāsu yaḥ |
notpādayed yadi ratiṁ
śrama eva hi kevalam ||`,
        translation:
            'Varṇāśrama-dharma of the human being (<strong>dharmaḥ svanuṣṭhitaḥ puṁsāṁ</strong>), which does not produce attraction for topics of the Lord (<strong>viṣvaksena-kathāsu yaḥ notpādayed yadi ratiṁ</strong>), is only wasted effort (<strong>śrama eva hi kevalam</strong>).',
    },
    blocks: [
        {
            type: 'text',
            content:
                'Why is dharma in the form of varṇāśrama not considered to be paro dharma? This verse answers. That dharma undertaken by men-brāhmaṇas, kṣatriyas, vaiśyas and śūdras which does not produce attraction for talks about the Supreme Lord is completely useless.<br /><br />The śruti says karmaṇā pirtṛ-loke: by performance of varṇāśrama duties one goes to Pitṛ-loka in the material world. (Bṛhad Āraṇyaka Upaniṣad 1.5)<br /><br /><strong>Performance of varṇāśrama duties does not produce attraction for the Lord. This is clear from Nārada’s statements in the Fourth Canto:</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 4.31.10',
                sloka: `kiṁ janmabhis tribhir veha
śaukra-sāvitra-yājñikaiḥ
karmabhir vā trayī-proktaiḥ
puṁso ’pi vibudhāyuṣā`,
                translation:
                    'Without that service what is the use of taking three births by semen, upanayanam and initiation. What is the use of ritual acts mentioned in the Vedas? What is the use of the lifespan of a devatā for a human?',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 4.31.12',
                sloka: `kiṁ vā yogena sāṅkhyena
nyāsa-svādhyāyayor api
kiṁ vā śreyobhir anyaiś ca
na yatrātma-prado hariḥ`,
                translation:
                    'What is the use of yoga, sāṅkhya, sannyāsa, study of the Vedas, other auspicious acts, in which the Lord does not give realization of himself?',
            },
        },
        {
            type: 'text',
            content:
                'Because it does not produce attraction for topics about the Lord it is merely useless endeavor.<br /><br /><strong>Because of the temporary nature of the results of performing varṇāśrama duties such as attainment of Pitṛ-loka, one should give up those duties (sva-dharma) and perform the paro dharma mentioned in the previous verse consisting of hearing and chanting about the Lord.</strong>',
        },
        {
            type: 'text',
            content: 'However it is also said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.20.11',
                sloka: `asmiṁ loke vartamānaḥ
sva-dharma-stho ’naghaḥ śuciḥ |
jñānaṁ viśuddham āpnoti
mad-bhaktiṁ ca yadṛcchayā ||`,
                translation:
                    'One who is situated in his prescribed duty, free from sinful activities and cleansed of material contamination, in this very life obtains transcendental knowledge or, by fortune, devotional service unto Me.',
            },
        },
        {
            type: 'dialogue',
            objection: 'The Lord himself says that karma is the cause of bhakti. How can you say that bhakti is without cause?',
            response:
                '<strong>That is true. It can be explained in that statement that karma gives rise to jñāna, but does not directly produce bhakti. That is understood from the use of the word yadṛcchayā (by itself) in the verse. Pure bhakti is independent (yadṛcchayā), indifferent to other processes. If by good fortune it happens to appear in a person, then he attains attraction for the Lord’s topics. The meaning of yadṛcchayā is “by itself.” By explaining the verse in another way, the self-manifesting nature of bhakti would be lost. Therefore such meanings are rejected. Thus even niṣkāma-karma is not the cause of bhakti. That is said in the present verse.<br /><br />Paro dharma is understood from verse 1.2.6 (sa vai puṁsām paro dharmaḥ). All dharmas other than that, undertaken as one’s duties (svanuṣṭhitaḥ), even if they are niṣkāma, and which do not produce affection (ratim) for topics about the Lord, are only useless labor.<br /><br />The word yadi here indicates disgust generated from the wasted labor. Medinī says yadi indicates disgust or doubt. Or yadi can be used to express doubt where no doubt exists as in the example yadi vedāḥ pramāṇam: if the Vedas are proof. Or in the sentence dhatte padam tvam avitā yadi vighna-mūrdhni: You, the protector, place your foot on the head of obstacles, yadi expresses certainty. This is how Śrīdhara Svāmī explains yadi.<br /><br />Another meaning is as follows. It is said that from this varṇāśrama sometimes attraction for topics of the Lord does arise. That is true. Because one cannot attain the results of dharma without such affection for the Lord’s topics, that affection is present but that is an appearance only, not genuine. If activities of varṇāśrama-dharma, whether kāmya (for fulfilling personal desires) or nitya (daily obligations), do not produce affection for the Lord’s topics, they are a waste of labor (for no material results will come).<br /><br />For farmers, agriculture must generate affection for the king; otherwise they cannot attain its results. Intelligent people, seeing that there will be no results without attraction for those topics, perform dharma that produces affection for the topics of the Lord. If the foolish perform the duties without producing affection for topics of the Lord, they labor for nothing. Just as it is wasted labor if one cannot attain the results of farming because of lack of loyalty to the king, it is wasted labor if one cannot get the results of Svarga and knowledge because of lack of devotion to the Lord.</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.12',
                sloka: `naiṣkarmyam apy acyuta-bhāva-varjitaṁ
na śobhate jñānam alaṁ nirañjanam
kutaḥ punaḥ śaśvad abhadram īśvara
na cārpitaṁ karma yad apy akāraṇam`,
                translation:
                    'Knowledge of self-realization, even though free from all material affinity, does not look well if devoid of a conception of the Infallible [God]. What, then, is the use of fruitive activities, which are naturally painful from the very beginning and transient by nature, if they are not utilized for the devotional service of the Lord?',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Just as affection for the king arises only because of attachment to agriculture, so attraction to topics of the Lord arises only because of attachment to varṇāśrama-dharma and its material results. Therefore that attachment to the topics of the Lord is not genuine.</strong>',
        },
        {
            type: 'text',
            content: '<strong>Prahlāda has expressed a pure relationship:</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 7.10.6',
                sloka: `ahaṁ tv akāmas tvad-bhaktas
tvaṁ ca svāmy anapāśrayaḥ
nānyathehāvayor artho
rāja-sevakayor iva`,
                translation:
                    'I am your servant, not desiring benefits, and you are my master, without dependence on service. Our relationship should not be anything else--like that of king and servant.',
            },
        },
    ],
};

export default content;
